/**
 * Web authentication strategy implementation
 * Handles authentication changes in web applications
 */

import { IAuthStrategy } from './IAuthStrategy';
import { IContainer } from '../../../../core/containers';
import { internalLogger } from '../../../../interface/v1/clientOsLogger';
import bindAllMethods from '../../../../utils/bindAllMethods';
import multiChannelEventManager, {
  MultiChannelEvents
} from '../../../../services/session/MultiChannelEvents';

/**
 * Configuration parameters for web authentication strategy
 */
export interface IWebAuthStrategyParams {
  authRefreshContainer: IContainer;
  webUIRefreshOnAuthChange: boolean;
}

/**
 * Web authentication strategy implementation
 * Follows Single Responsibility Principle by focusing solely on web auth handling
 */
export class WebAuthStrategy implements IAuthStrategy {
  private authRefreshContainer: IContainer;
  private webUIRefreshOnAuthChange: boolean;

  constructor({
    authRefreshContainer,
    webUIRefreshOnAuthChange
  }: IWebAuthStrategyParams) {
    bindAllMethods(this);

    this.authRefreshContainer = authRefreshContainer;
    this.webUIRefreshOnAuthChange = webUIRefreshOnAuthChange;
  }

  /**
   * Initializes the web authentication strategy
   */
  public async init(): Promise<void> {
    if (!this.webUIRefreshOnAuthChange) {
      /* istanbul ignore next */
      internalLogger?.debug?.d4({
        className: 'WebAuthStrategy',
        methodName: 'init',
        message:
          'Web UI refresh on auth change is disabled, not subscribing to session events.',
        variables: { webUIRefreshOnAuthChange: this.webUIRefreshOnAuthChange }
      });
      return;
    }

    // Subscribe to session changes for web UI
    await this.subscribeToLoginChanges();
  }

  /**
   * Handles authentication status changes for web applications
   */
  public async handleAuthStatusChange(): Promise<void> {
    //@TODO: If needed, we can check if the current path is not loggedin here, to avoid double refreshing.
    /* istanbul ignore next */
    internalLogger?.debug?.d2({
      className: 'WebAuthStrategy',
      methodName: 'handleAuthStatusChange',
      message:
        'Login Change event received, updating Web UI state and refreshing auth container.',
      parameters: {}
    });
    await this.authRefreshContainer.refreshContext();
  }

  /**
   * Subscribes to login change events
   */
  private async subscribeToLoginChanges(): Promise<void> {
    // Subscribe to authentication change events
    /* istanbul ignore next */
    internalLogger?.debug?.d2({
      className: 'WebAuthStrategy',
      methodName: 'subscribeToLoginChanges',
      message: 'Subscribing to login changes for web auth strategy.',
      parameters: {}
    });
    multiChannelEventManager.subscribe(
      MultiChannelEvents.USER_LOGGED_IN,
      this.handleAuthStatusChange
    );

    // @TODO: add logout event
  }
}
